import { useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../../app";
import { ADMIN_ROLE_ID, getVariableFromEnv } from "../../common/utilities/env";
import { notification } from "antd";
import { useTranslation } from "react-i18next";
import selectModalStateByKey from "../../app/selectors/modalStateSelector";
import { changeModalStateForKey } from "../../app/store/appSlice";
import { useInviteMemberMutation } from "../store/UserManagementApi";
import Sentry from "../../sentry";

const modalKey = "userManagementAddMembers";

const useMemberModal = () => {
  const dispatch = useAppDispatch();

  const { t } = useTranslation("userManagement");

  const open = useAppSelector((state) =>
    selectModalStateByKey(state, { modalKey }),
  );

  const { members } = useAppSelector((state) => state.userManagementReducer);
  const { user } = useAppSelector((state) => state.userReducer);

  const [inviteMember] = useInviteMemberMutation();

  const handleInviteMember = useCallback(
    async (email: string, permissions: Array<string>) => {
      if (!user?.access.includes(ADMIN_ROLE_ID)) {
        return;
      }

      if (!user?.organization.id) {
        return;
      }

      if (members.find((m) => m.email === email)) {
        return;
      }

      try {
        await inviteMember({
          organizationId: user.organization.id,
          clientId: getVariableFromEnv("AUTH_CLIENT_ID"),
          email,
          permissions,
        }).unwrap();

        dispatch(changeModalStateForKey(modalKey));
        notification.success({
          title: t("addModal.successMessage.message"),
          description: t("addModal.successMessage.message").replace(
            "$MEMBER",
            email,
          ),
        });
      } catch (e) {
        dispatch(changeModalStateForKey(modalKey));

        if ((e as { status?: number })?.status === 422) {
          notification.error({
            title: t("addModal.outstandingInviteError.message"),
            description: t(
              "addModal.outstandingInviteError.description",
            ).replace("$MEMBER", email),
          });
        } else {
          notification.error({
            title: t("addModal.errorMessage.message"),
            description: t("addModal.errorMessage.description"),
          });
        }

        console.log(e);
        Sentry.captureException(e, {
          tags: {
            flow: "invite_member",
          },
          extra: {
            email,
            organizationId: user?.organization.id,
          },
        });
      }
    },
    [user, members, t, dispatch, inviteMember],
  );

  const changeModalOpen = useCallback(() => {
    dispatch(changeModalStateForKey(modalKey));
  }, [dispatch]);

  return { open, changeModalOpen, handleInviteMember };
};

export default useMemberModal;
